import {
  ArrowBackOutlined,
  CloseFullscreenOutlined,
  CloseRounded,
  OpenInFullOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  useMediaQuery,
  type Theme,
} from '@mui/material';
import { designTokens } from '../tokens';
import type { WorkspaceSurfacePresentation } from './FormDialog';
import { SaveStatus, type SaveStatusDescriptor, type SaveStatusTone } from './SaveStatus';

export type WorkspaceSurfaceStatusTone = SaveStatusTone;
export type WorkspaceSurfaceStatusDescriptor = SaveStatusDescriptor;

export type WorkspaceSurfaceHeaderActionsProps = {
  presentation: WorkspaceSurfacePresentation;
  onPresentationChange?: (presentation: WorkspaceSurfacePresentation) => void;
  onExit: () => void;
  status?: WorkspaceSurfaceStatusDescriptor;
  /** 항상 노출되는 헤더에서는 모바일도 작은 아이콘 작업으로 유지합니다. */
  compact?: boolean;
  presentationDisabled?: boolean;
  exitDisabled?: boolean;
  ariaLabel?: string;
  enterFullscreenLabel?: string;
  exitFullscreenLabel?: string;
  dialogExitLabel?: string;
  standaloneExitLabel?: string;
};

/**
 * 긴 작성 surface의 상태와 presentation·exit action을 한 compact group으로 표시합니다.
 * desktop은 tooltip이 있는 icon action, mobile page action menu는 text row를 사용합니다.
 */
export function WorkspaceSurfaceHeaderActions({
  presentation,
  onPresentationChange,
  onExit,
  status,
  compact = false,
  presentationDisabled = false,
  exitDisabled = false,
  ariaLabel = '작성 화면 작업',
  enterFullscreenLabel = '전체 화면으로 열기',
  exitFullscreenLabel = '팝업으로 돌아가기',
  dialogExitLabel = '팝업 닫기',
  standaloneExitLabel = '워크플로우로 돌아가기',
}: WorkspaceSurfaceHeaderActionsProps) {
  const smallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const mobile = smallScreen && !compact;
  const standalone = presentation === 'standalone';
  const presentationLabel = standalone ? exitFullscreenLabel : enterFullscreenLabel;
  const exitLabel = standalone ? standaloneExitLabel : dialogExitLabel;
  const presentationIcon = standalone ? <CloseFullscreenOutlined /> : <OpenInFullOutlined />;
  const exitIcon = standalone ? <ArrowBackOutlined /> : <CloseRounded />;

  return <Stack
    role={mobile ? undefined : 'group'}
    aria-label={mobile ? undefined : ariaLabel}
    direction={mobile ? 'column' : 'row'}
    spacing={mobile ? 0 : 0.5}
    sx={{
      alignItems: mobile ? 'stretch' : 'center',
      minWidth: 0,
      ...(mobile ? undefined : {
        height: `${designTokens.layout.pageHeaderControlSize}px`,
        minHeight: `${designTokens.layout.pageHeaderControlSize}px`,
        [designTokens.media.touchTarget]: { height: designTokens.layout.touchTargetMinSize, minHeight: designTokens.layout.touchTargetMinSize },
        flexWrap: 'nowrap',
        whiteSpace: 'nowrap',
      }),
    }}
  >
    {status ? <WorkspaceSurfaceStatus status={status} mobile={mobile} /> : null}
    {!smallScreen && onPresentationChange ? <Tooltip title={presentationLabel}>
      <span>
        <IconButton
          aria-label={presentationLabel}
          disabled={presentationDisabled}
          onClick={() => onPresentationChange(standalone ? 'dialog' : 'standalone')}
          sx={workspaceIconActionSx}
        >{presentationIcon}</IconButton>
      </span>
    </Tooltip> : null}
    {mobile ? <Button
      size="small"
      startIcon={exitIcon}
      aria-label={exitLabel}
      disabled={exitDisabled}
      onClick={onExit}
    >{exitLabel}</Button> : <Tooltip title={exitLabel}>
      <span>
        <IconButton
          aria-label={exitLabel}
          disabled={exitDisabled}
          onClick={onExit}
          sx={workspaceIconActionSx}
        >{exitIcon}</IconButton>
      </span>
    </Tooltip>}
  </Stack>;
}

function WorkspaceSurfaceStatus({ status, mobile }: { status: WorkspaceSurfaceStatusDescriptor; mobile: boolean }) {
  return <Box
    data-testid="workspace-surface-status"
    sx={{
      minWidth: 0,
      minHeight: mobile ? designTokens.layout.pageHeaderControlSizeCompact : designTokens.layout.pageHeaderControlSize,
      height: mobile ? 'auto' : `${designTokens.layout.pageHeaderControlSize}px`,
      [designTokens.media.touchTarget]: { minHeight: designTokens.layout.touchTargetMinSize },
      px: mobile ? 1.5 : 0.75,
      mr: mobile ? 0 : 0.25,
      display: 'flex',
      alignItems: 'center',
    }}
  >
    <SaveStatus status={status} />
  </Box>;
}

const workspaceIconActionSx = {
  border: `1px solid ${designTokens.color.divider}`,
  '&:hover': {
    borderColor: designTokens.color.strongDivider,
  },
} as const;
